import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { getCleanedUrlParams, showError } from '@utils';
import { BOARDS_ID, GENERIC_ERROR_MESSAGE } from '@constants';
import { boardsServices } from '@services';

import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees } from 'hooks/common';
import {
  BUYOUT_LIMIT,
  normalizeBuyOutData,
  getBuyOutsColumnsState,
  getBuyOutParamsAndPayload,
} from './utils';

export const useBuyOutOperations = (fetchOnMount) => {
  const router = useRouter();
  const { assignees, isLoading: isAssigneesLoading } = useAssignees();

  const {
    isReady,
    query: {
      page = 1,
      pageSize = BUYOUT_LIMIT,
      orderBy = 'stage',
      order = 'asc',
      token,
      query: searchQuery,
      ...others
    },
  } = router;

  const { exportParams, payload } = useMemo(
    () =>
      getBuyOutParamsAndPayload({ order, orderBy, query: searchQuery, others }),
    [order, orderBy, searchQuery, others]
  );

  const _otherQueries = Object.entries(others)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const {
    data: boardFiltersData,
    isLoading: isBoardFiltersLoading,
    isError: isBoardFiltersError,
    error: boardFiltersError,
    refetch: refetchBoardFilters,
  } = useQuery(
    [
      QUERY_KEYS.GET_BUY_OUT_FILTERS,
      {
        page,
        pageSize,
        orderBy,
        order,
        token,
        query: searchQuery,
        _otherQueries,
        payload,
      },
    ],
    () => {
      const urlParams = getCleanedUrlParams({
        page,
        orderBy,
        order,
        pageSize: parseInt(pageSize, 10) || BUYOUT_LIMIT,
        query: searchQuery,
      });
      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.BUY_OUT,
        params: urlParams,
        payload,
      });
    },
    {
      enabled: !!fetchOnMount && !!isReady,
      refetchOnMount: false,
    }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, BOARDS_ID.BUY_OUT],
    () => boardsServices.getEnumsPerBoardId(BOARDS_ID.BUY_OUT),
    { enabled: router.isReady }
  );

  const normalizedBuyOutData = useMemo(() => {
    if (!boardFiltersData?.data || !assignees) return [];
    return normalizeBuyOutData(boardFiltersData.data, assignees);
  }, [boardFiltersData?.data, assignees]);

  const columns = useMemo(
    () =>
      normalizedBuyOutData
        ? getBuyOutsColumnsState({ ...normalizedBuyOutData })
        : [],
    [normalizedBuyOutData]
  );

  const total = boardFiltersData?.meta?.total;

  const loading = isBoardFiltersLoading || isAssigneesLoading;

  const hasErrors = isBoardFiltersError;

  const errorMessage =
    boardFiltersError || hasErrors ? GENERIC_ERROR_MESSAGE : null;

  useEffect(() => {
    if (errorMessage) {
      showError(errorMessage);
    }
  }, [errorMessage]);

  return {
    page,
    pageSize: parseInt(pageSize, 10) || BUYOUT_LIMIT,
    total,
    loading,
    buyOut: normalizedBuyOutData,
    columns,
    hasErrors,
    errorMessage,
    buyOutQueryParams: exportParams,
    buyOutPayload: payload,
    enums: enumsResponse?.data?.data || {},
    getBuyOut: refetchBoardFilters,
  };
};
